import { AssetsProvider, getHomeUrl, useTranslation } from '@obvio/app'
import { serverClient } from '@obvio/server'

import { schema } from '@/api/schema'
import { ARTICLE_SELECT, Post } from '@/components/Blog/Post/Post'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { JsonLd } from '@/components/JsonLd'
import { Layout } from '@/components/Layout'
import { PageSeo } from '@/components/PageSeo'
import { REVALIDATE_SECONDS } from '@/constants/revalidate'
import { blogPostingJsonLd } from '@/utils/jsonLd'
import {
  describeRichText,
  localizedUrl,
  toSiteLocale,
  withSiteName,
} from '@/utils/seo'

import type { PostProps } from '@/components/Blog/Post/Post'
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import type { ReactElement } from 'react'

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ slug: string }>,
): Promise<GetStaticPropsResult<PostProps>> {
  const client = await serverClient(schema, { isIsr: true, ...ctx })
  const article = await client.query('getArticle', {
    args: {
      slug: ctx.params?.slug ?? '',
    },
    select: ARTICLE_SELECT,
  })

  if (!article) {
    return {
      notFound: true,
    }
  }

  const normalizedArticle = {
    ...article,
    createdAt: article.createdAt.toISOString(),
  }

  return {
    ...client.ssrData({ article: normalizedArticle }),
    revalidate: REVALIDATE_SECONDS,
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = await serverClient(schema, { isIsr: true })
  const articles = await client.query('getArticles', { select: { slug: true } })

  const slugs = articles.map(({ slug }) => ({ params: { slug } }))

  return {
    paths: slugs,
    fallback: 'blocking',
  }
}

function Page({ article }: PostProps): ReactElement {
  const { locale } = useTranslation()
  const siteLocale = toSiteLocale(locale)
  const path = `blog/post/${article.slug}`

  const title = article.seoTitle || withSiteName(article.title)
  const firstParagraph = article.content.find(
    (section: unknown): section is string => typeof section === 'string',
  )
  const description = article.seoDescription || describeRichText(firstParagraph)
  const image = article.image
    ? `${getHomeUrl()}/article/${article.id}/${article.image.name}`
    : undefined

  return (
    <AssetsProvider path={`/article/${article.id}`}>
      <PageSeo
        path={path}
        title={title}
        description={description}
        type="article"
      />
      <Breadcrumbs
        trail={[
          { name: 'Blog', path: 'blog' },
          { name: article.title, path },
        ]}
      />
      <JsonLd
        id="blog-posting"
        data={blogPostingJsonLd({
          url: localizedUrl(path, siteLocale),
          headline: article.title,
          description,
          image,
          datePublished: article.createdAt,
          inLanguage: siteLocale,
        })}
      />

      <Layout type="Główny">
        <Post article={article} />
      </Layout>
    </AssetsProvider>
  )
}

export default Page
